import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getProductsPage, TIENDA_PRODUCTOS_PAGE_SIZE } from '../../../services/products';
import { getCategories } from '../../../services/categories';
import { getBrands } from '../../../services/brands';
import ProductCard from './ProductCard';
import './ProductList.css';

/** Hasta `max` números centrados alrededor de la página actual. */
function visiblePageNumbers(current, total, max = 7) {
  if (total <= max) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  let start = Math.max(1, current - Math.floor(max / 2));
  let end = start + max - 1;
  if (end > total) {
    end = total;
    start = Math.max(1, end - max + 1);
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function ProductList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const marcaFilter = searchParams.get('marca') || '';

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [bootLoading, setBootLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceSort, setPriceSort] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filtersMobileOpen, setFiltersMobileOpen] = useState(false);

  const ordering = useMemo(() => {
    if (priceSort === 'asc') return 'precio_minimo';
    if (priceSort === 'desc') return '-precio_minimo';
    return undefined;
  }, [priceSort]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /** Solo quitar `page` de la URL cuando el texto de búsqueda (debounced) cambia de verdad — evita borrar ?page= al paginar. */
  const prevDebouncedSearch = useRef(undefined);
  useEffect(() => {
    if (prevDebouncedSearch.current === undefined) {
      prevDebouncedSearch.current = debouncedSearch;
      return;
    }
    if (prevDebouncedSearch.current === debouncedSearch) return;
    prevDebouncedSearch.current = debouncedSearch;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      return next;
    }, { replace: true });
  }, [debouncedSearch, setSearchParams]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    const closeIfDesktop = () => {
      if (mq.matches) setFiltersMobileOpen(false);
    };
    closeIfDesktop();
    mq.addEventListener('change', closeIfDesktop);
    return () => mq.removeEventListener('change', closeIfDesktop);
  }, []);

  useEffect(() => {
    Promise.all([getCategories(), getBrands()])
      .then(([catData, brandData]) => {
        setCategories(catData);
        setBrands(brandData);
      })
      .catch((err) => console.error('Error cargando filtros:', err))
      .finally(() => setBootLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    getProductsPage({
      page,
      marca: marcaFilter || undefined,
      categoria: selectedCategory || undefined,
      search: debouncedSearch || undefined,
      ordering,
    })
      .then((data) => {
        if (cancelled) return;
        setProducts(data.results || []);
        setTotalCount(typeof data.count === 'number' ? data.count : (data.results || []).length);
      })
      .catch((err) => console.error('Error cargando productos:', err))
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, marcaFilter, selectedCategory, debouncedSearch, ordering]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [page, marcaFilter, selectedCategory, debouncedSearch, ordering]);

  const totalPages = Math.max(1, Math.ceil(totalCount / TIENDA_PRODUCTOS_PAGE_SIZE) || 1);
  const pageNumbers = visiblePageNumbers(page, totalPages, 7);

  const goToPage = useCallback((p) => {
    const nextPage = Math.min(Math.max(1, p), totalPages);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage <= 1) next.delete('page');
      else next.set('page', String(nextPage));
      return next;
    }, { replace: true });
  }, [totalPages, setSearchParams]);

  const clearPageOnly = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      return next;
    }, { replace: true });
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setPriceSort('');
    setSearchTerm('');
    setDebouncedSearch('');
    prevDebouncedSearch.current = undefined;
    setSearchParams({}, { replace: true });
  };

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * TIENDA_PRODUCTOS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * TIENDA_PRODUCTOS_PAGE_SIZE, totalCount);

  if (bootLoading) {
    return (
      <div className="product-list-page">
        <p className="product-list-loading">Cargando catálogo...</p>
      </div>
    );
  }

  const resultsKey = `${page}|${marcaFilter}|${selectedCategory}|${debouncedSearch}|${ordering || ''}`;

  return (
    <div className="product-list-page">
      <div
        className={`product-filters-wrapper${filtersMobileOpen ? ' product-filters-wrapper--open' : ''}`}
      >
        <div className="product-filters-top">
          <input
            type="search"
            enterKeyHint="search"
            placeholder="Buscar…"
            className="product-filter-input"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            aria-label="Buscar producto"
          />
          <button
            type="button"
            className="product-filters-toggle"
            aria-expanded={filtersMobileOpen}
            onClick={() => setFiltersMobileOpen((v) => !v)}
          >
            {filtersMobileOpen ? 'Cerrar' : 'Filtros'}
          </button>
        </div>
        <div className="product-filters-panel">
          <select
            className="product-filter-select"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              clearPageOnly();
            }}
            aria-label="Categoría"
          >
            <option value="">Todas las categorías</option>
            {categories.map((cat) => (
              <option key={cat.leora_categorias_UUID} value={cat.leora_categorias_UUID}>
                {cat.nombre}
              </option>
            ))}
          </select>

          <select
            className="product-filter-select"
            value={marcaFilter}
            onChange={(e) => {
              const v = e.target.value;
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (v) next.set('marca', v);
                else next.delete('marca');
                next.delete('page');
                return next;
              }, { replace: true });
            }}
            aria-label="Marca"
          >
            <option value="">Todas las marcas</option>
            {brands.map((b) => (
              <option key={b.leora_marcas_UUID} value={b.leora_marcas_UUID}>
                {b.nombre}
              </option>
            ))}
          </select>

          <select
            className="product-filter-select"
            value={priceSort}
            onChange={(e) => {
              setPriceSort(e.target.value);
              clearPageOnly();
            }}
            aria-label="Ordenar por precio"
          >
            <option value="">Ordenar por precio</option>
            <option value="asc">Menor a mayor</option>
            <option value="desc">Mayor a menor</option>
          </select>

          <button type="button" className="product-filter-clear" onClick={clearFilters}>
            Limpiar
          </button>
        </div>
      </div>

      <div className={`product-grid-wrap${listLoading ? ' product-grid-wrap--loading' : ''}`}>
        <div key={resultsKey} className="product-grid product-grid--enter">
          {products.map((product) => (
            <ProductCard key={product.leora_productos_UUID} product={product} />
          ))}
        </div>
      </div>

      {products.length === 0 && !listLoading && (
        <p className="product-list-empty">No se encontraron productos con esos filtros.</p>
      )}

      <footer className="product-list-footer">
        <p className="product-list-count">
          {totalCount > 0
            ? `${rangeStart}–${rangeEnd} de ${totalCount} ${totalCount === 1 ? 'producto' : 'productos'}`
            : '0 productos'}
        </p>
        {totalPages > 1 && (
          <nav className="product-pagination" aria-label="Paginación de productos">
            {page > 1 && (
              <button
                type="button"
                className="product-pagination-prev"
                onClick={() => goToPage(page - 1)}
              >
                &lt; Anterior
              </button>
            )}
            <div className="product-pagination-pages">
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={
                    n === page
                      ? 'product-pagination-num product-pagination-num--active'
                      : 'product-pagination-num'
                  }
                  onClick={() => goToPage(n)}
                  aria-current={n === page ? 'page' : undefined}
                >
                  {n}
                </button>
              ))}
            </div>
            {page < totalPages && (
              <button
                type="button"
                className="product-pagination-next"
                onClick={() => goToPage(page + 1)}
              >
                Siguiente &gt;
              </button>
            )}
          </nav>
        )}
      </footer>
    </div>
  );
}

export default ProductList;
